import React from 'react';
import { X, GitBranch, ArrowRight, ArrowLeft, Layers, Hash, AlertTriangle } from 'lucide-react';
import { LdgNodeData } from './useLdgGraphData';
interface NodeDetailPanelProps {
  nodeData: LdgNodeData | null;
  onClose: () => void;
  onSelectNode: (nodeId: string) => void;
}
export function NodeDetailPanel({
  nodeData,
  onClose,
  onSelectNode
}: NodeDetailPanelProps) {
  if (!nodeData) return null;
  const {
    colorSet
  } = nodeData;
  return <>
      {/* Mobile: Bottom sheet */}
      <div className="md:hidden absolute inset-x-0 bottom-0 max-h-[60vh] bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 flex flex-col z-50 overflow-hidden" data-id="element-2810">
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0" data-id="element-2811">
          <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-2812" />
        </div>

        {/* Header */}
        <div className={`px-4 py-3 border-b border-slate-100 flex items-start justify-between ${colorSet.light}`} data-id="element-2813">
          <div className="min-w-0 flex-1" data-id="element-2814">
            <div className="flex items-center gap-2 mb-0.5" data-id="element-2815">
              <div className={`w-2 h-2 rounded-full ${colorSet.bg}`} data-id="element-2816" />
              <span className={`text-[10px] font-bold uppercase tracking-wider ${colorSet.text}`} data-id="element-2817">
                {nodeData.module}
              </span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colorSet.bg} text-white`} data-id="element-2818">
                {nodeData.levelCode}
              </span>
            </div>
            <h3 className="font-mono font-bold text-slate-900 text-base leading-tight truncate" data-id="element-2819">
              {nodeData.label}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0 ml-2" data-id="element-2820">
            <X className="w-5 h-5" data-id="element-2821" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-2822">
          {!nodeData.hasContent && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2" data-id="element-2823">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-2824" />
              <p className="text-xs text-amber-700" data-id="element-2825">
                Geen content — phantom node.
              </p>
            </div>}

          {/* Compact properties */}
          <div className="flex items-center gap-3 text-xs" data-id="element-2826">
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100" data-id="element-2827">
              <Layers className="w-3 h-3 text-slate-400" data-id="element-2828" />
              <span className="font-bold text-slate-700" data-id="element-2829">
                Tier {nodeData.tier}
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100" data-id="element-2830">
              <Hash className="w-3 h-3 text-slate-400" data-id="element-2831" />
              <span className="text-slate-600 truncate max-w-[120px]" data-id="element-2832">
                {nodeData.skill}
              </span>
            </div>
          </div>

          {/* Prerequisites */}
          {nodeData.prerequisites.length > 0 && <div data-id="element-2833">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-2834">
                <ArrowRight className="w-3 h-3" data-id="element-2835" />
                Vereist ({nodeData.prerequisites.length})
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-2836">
                {nodeData.prerequisites.map(prereq => <button key={prereq} onClick={() => onSelectNode(prereq)} className="font-mono text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors" data-id="element-2837">
                    {prereq}
                  </button>)}
              </div>
            </div>}

          {/* Dependents */}
          {nodeData.dependents.length > 0 && <div data-id="element-2838">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-2839">
                <GitBranch className="w-3 h-3" data-id="element-2840" />
                Ontsluit ({nodeData.dependents.length})
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-2841">
                {nodeData.dependents.map(dep => <button key={dep} onClick={() => onSelectNode(dep)} className="font-mono text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors" data-id="element-2842">
                    {dep}
                  </button>)}
              </div>
            </div>}

          {nodeData.prerequisites.length === 0 && nodeData.dependents.length === 0 && <p className="text-xs text-slate-500 italic" data-id="element-2843">
                Geïsoleerde node (geen verbindingen).
              </p>}
        </div>
      </div>

      {/* Desktop: Right side panel */}
      <div className="hidden md:flex absolute top-4 right-4 bottom-4 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 flex-col z-50 overflow-hidden" data-id="element-2844">
        {/* Header */}
        <div className={`p-4 border-b border-slate-100 flex items-start justify-between ${colorSet.light}`} data-id="element-2845">
          <div data-id="element-2846">
            <div className="flex items-center gap-2 mb-1" data-id="element-2847">
              <div className={`w-2.5 h-2.5 rounded-full ${colorSet.bg}`} data-id="element-2848" />
              <span className={`text-xs font-bold uppercase tracking-wider ${colorSet.text}`} data-id="element-2849">
                {nodeData.module}
              </span>
            </div>
            <h3 className="font-mono font-bold text-slate-900 text-lg leading-tight" data-id="element-2850">
              {nodeData.label}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-lg transition-colors" data-id="element-2851">
            <X className="w-5 h-5" data-id="element-2852" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-2853">
          {!nodeData.hasContent && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5" data-id="element-2854">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-2855" />
              <div data-id="element-2856">
                <h4 className="text-sm font-bold text-amber-800" data-id="element-2857">
                  Geen content
                </h4>
                <p className="text-xs text-amber-700 mt-0.5" data-id="element-2858">
                  Deze family heeft nog geen vragen in de database. Het is een
                  "phantom node" in de graaf.
                </p>
              </div>
            </div>}

          {/* Properties */}
          <div className="grid grid-cols-2 gap-3" data-id="element-2859">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-2860">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-2861">
                <Layers className="w-3 h-3" data-id="element-2862" />
                Tier
              </div>
              <div className="font-bold text-slate-900 text-lg" data-id="element-2863">
                {nodeData.tier}
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-2864">
                Niveau {nodeData.levelCode}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-2865">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5" data-id="element-2866">
                <Hash className="w-3 h-3" data-id="element-2867" />
                Skill
              </div>
              <div className="font-medium text-slate-900 text-sm truncate" title={nodeData.skill} data-id="element-2868">
                {nodeData.skill}
              </div>
              <div className="text-xs text-slate-500 truncate" title={nodeData.subSkill} data-id="element-2869">
                {nodeData.subSkill}
              </div>
            </div>
          </div>

          {/* Prerequisites */}
          <div data-id="element-2870">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2871">
              <ArrowRight className="w-4 h-4 text-slate-300" data-id="element-2872" />
              Vereist ({nodeData.prerequisites.length})
            </h4>
            {nodeData.prerequisites.length === 0 ? <div className="text-sm text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-2873">
                Dit is een root node (geen vereisten).
              </div> : <div className="space-y-2" data-id="element-2874">
                {nodeData.prerequisites.map(prereq => <button key={prereq} onClick={() => onSelectNode(prereq)} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group flex items-center justify-between" data-id="element-2875">
                    <span className="font-mono text-sm text-slate-700 group-hover:text-indigo-700 transition-colors truncate" data-id="element-2876">
                      {prereq}
                    </span>
                    <ArrowLeft className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" data-id="element-2877" />
                  </button>)}
              </div>}
          </div>

          {/* Dependents */}
          <div data-id="element-2878">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2879">
              <GitBranch className="w-4 h-4 text-slate-300" data-id="element-2880" />
              Ontsluit ({nodeData.dependents.length})
            </h4>
            {nodeData.dependents.length === 0 ? <div className="text-sm text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-2881">
                Dit is een leaf node (ontsluit niets).
              </div> : <div className="space-y-2" data-id="element-2882">
                {nodeData.dependents.map(dep => <button key={dep} onClick={() => onSelectNode(dep)} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group flex items-center justify-between" data-id="element-2883">
                    <span className="font-mono text-sm text-slate-700 group-hover:text-indigo-700 transition-colors truncate" data-id="element-2884">
                      {dep}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" data-id="element-2885" />
                  </button>)}
              </div>}
          </div>
        </div>
      </div>
    </>;
}